import { html, nothing, svg, type TemplateResult } from "lit";
import type { EventsOptions } from "../../config/types";
import type { MockEvent } from "../data/mock-events";
import { formatMajorAmount, formatShortAmount } from "../format-money";

const ICON_PIN = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
const ICON_ARROW = svg`<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`;
const ICON_BACK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>`;
const ICON_CAL = svg`<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;
const ICON_EXTERNAL = svg`<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;
const ICON_DOWN = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>`;

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

export interface EventCardViewModel {
  options: EventsOptions;
  registrationClosed?: boolean;
  thumbnailRatio?: string;
  description: string;
  startTime: string;
  endTime: string;
  timezoneAbbreviation: string;
  /** Org ISO-4217 currency; USD when unset. */
  currency?: string | undefined;
  onOpen: () => void;
}

export function renderEventCard(
  event: MockEvent,
  model: EventCardViewModel,
): TemplateResult {
  const options = model.options;
  const sideHidden = options.hideRegister && options.hidePrice;
  const ticketPrice = event.externalTickets
    ? typeof event.advertisedTicketPrice === "number" &&
      Number.isFinite(event.advertisedTicketPrice) &&
      event.advertisedTicketPrice >= 0
      ? `${formatMajorAmount(event.advertisedTicketPrice, model.currency)} on ticket site`
      : "Tickets on external site"
    : event.requiresEntitlement
      ? "Membership or class credit"
      : event.price === 0
      ? "Free"
      : formatShortAmount(event.price, model.currency);
  return html`
    <div class="card">
      ${options.hideThumbnail
        ? nothing
        : html`<div
            class="thumb"
            style=${model.thumbnailRatio
              ? `--thumb-ratio:${model.thumbnailRatio}`
              : ""}
          >
            <img src=${event.image} alt="" loading="lazy" />
            ${options.hideDateTag
              ? nothing
              : html`<div class="date-tag">
                  <div class="m">${event.month}</div>
                  <div class="d">${event.day}</div>
                </div>`}
          </div>`}
      <div class="body">
        <div class="when">
          <span
            >${event.weekday}, ${event.month} ${event.day}, ${event.year}</span
          >
          <span class="sep">·</span>
          <span
            >${model.startTime} – ${model.endTime}
            (${model.timezoneAbbreviation})</span
          >
        </div>
        <div class="ename">${event.title}</div>
        ${options.hideDescription || !model.description
          ? nothing
          : html`<div class="summary">${model.description}</div>`}
        ${options.hideLocation || !hasText(event.location)
          ? nothing
          : html`<div class="loc">${ICON_PIN} ${event.location}</div>`}
      </div>
      ${sideHidden
        ? nothing
        : html`<div class="aside">
            ${options.hidePrice
              ? nothing
              : html`<div class="price">
                  ${ticketPrice}
                </div>`}
            ${options.hideRegister
              ? nothing
              : event.externalTickets
                ? event.externalTicketUrl
                  ? html`<a
                      class="reg"
                      href=${event.externalTicketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >Buy tickets ${ICON_EXTERNAL}</a>`
                  : html`<span class="reg" aria-disabled="true">Ticket link unavailable</span>`
                : html`<button class="reg" @click=${model.onOpen}>
                    ${model.registrationClosed ? "View details" : (options.buttonText ?? "Register")}${ICON_ARROW}
                  </button>`}
          </div>`}
    </div>
  `;
}

export interface EventDetailViewModel {
  startTime: string;
  registrationClosed?: boolean;
  endTime: string;
  timezoneAbbreviation: string;
  description: string;
  registration: TemplateResult;
  onBack: () => void;
  onScrollToRegistration: (event: Event) => void;
  onShare: () => void;
  onDownloadCalendar: () => void;
}

export function renderEventDetail(
  event: MockEvent,
  model: EventDetailViewModel,
): TemplateResult {
  const mapQuery = hasText(event.address) ? event.address : event.location;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    mapQuery,
  )}`;
  const hasLocation = hasText(event.location) || hasText(event.address);

  return html`
    <button class="back" @click=${model.onBack}>
      ${ICON_BACK} Back to events
    </button>
    ${event.image?.trim()
      ? html`<div class="hero"><img src=${event.image} alt="" /></div>`
      : nothing}
    <div class="detail-grid">
      <div>
        <h1 class="dtitle">${event.title}</h1>
        <div class="metas">
          <div class="meta">
            <span class="ic">${ICON_CAL}</span>
            <div>
              <div>
                ${event.weekday}, ${event.month} ${event.day}, ${event.year}
              </div>
              <div class="sub">
                ${model.startTime} – ${model.endTime}
                (${model.timezoneAbbreviation})
              </div>
            </div>
          </div>
          ${hasLocation
            ? html`<div class="meta">
                <span class="ic">${ICON_PIN}</span>
                <div>
                  ${hasText(event.location)
                    ? html`<div>${event.location}</div>`
                    : nothing}
                  ${hasText(event.address)
                    ? html`<div class="sub">${event.address}</div>`
                    : nothing}
                </div>
              </div>`
            : nothing}
        </div>
        ${event.externalTickets
          ? event.externalTicketUrl
            ? html`<a
                class="register-now"
                href=${event.externalTicketUrl}
                target="_blank"
                rel="noopener noreferrer"
              >Buy tickets ${ICON_EXTERNAL}</a>`
            : html`<span>Ticket link unavailable</span>`
          : model.registrationClosed ? nothing : html`<a class="register-now" href="#" @click=${model.onScrollToRegistration}
              >Register Now ${ICON_DOWN}</a
            >`}
        <div class="divider"></div>
        <h2 class="dh2">About this event</h2>
        <p class="dbody">${model.description}</p>
      </div>
      <aside class="aside-col">
        ${hasLocation
          ? html`<h2 class="dh2">Location</h2>
              <div class="loc-detail">
                ${hasText(event.location)
                  ? html`<div>${event.location}</div>`
                  : nothing}
                ${hasText(event.address)
                  ? html`<div class="sub">${event.address}</div>`
                  : nothing}
              </div>
              <a
                class="maplink"
                href=${mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on Google Maps ${ICON_EXTERNAL}
              </a>`
          : nothing}
        <div class="share-row">
          <button class="share" type="button" @click=${model.onShare}>
            Share
          </button>
          <button
            class="share"
            type="button"
            @click=${model.onDownloadCalendar}
          >
            Add to calendar
          </button>
        </div>
      </aside>
    </div>
    ${model.registration}
  `;
}
